import React, { FC, useRef, useState, useEffect, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import cn from '../../lib/utils/cn'

interface EllipsisTextProps {
	text: string
	className?: string
	tooltipLabel?: string
	tooltipPosition?: 'top' | 'bottom'
	withoutTooltip?: boolean
	leftIcon?: ReactNode | null
}

const EllipsisText: FC<EllipsisTextProps> = ({
	text,
	className,
	tooltipLabel,
	tooltipPosition = 'top',
	withoutTooltip = false,
	leftIcon,
}) => {
	const textRef = useRef<HTMLSpanElement>(null)
	const [isTruncated, setIsTruncated] = useState(false)
	const [showTooltip, setShowTooltip] = useState(false)
	const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({})

	useEffect(() => {
		const el = textRef.current
		if (el) {
			setIsTruncated(el.scrollWidth > el.offsetWidth)
		}
	}, [text])

	const shouldShowTooltip = !withoutTooltip && isTruncated
	const label = tooltipLabel || text

	const updateTooltipPosition = () => {
		if (!textRef.current || !shouldShowTooltip) return

		const rect = textRef.current.getBoundingClientRect()
		const scrollX = window.pageXOffset
		const scrollY = window.pageYOffset
		const tooltipHeight = 32
		const top =
			tooltipPosition === 'top'
				? rect.top + scrollY - tooltipHeight - 8
				: rect.bottom + scrollY + 8

		setTooltipStyle({
			position: 'absolute',
			left: rect.left + scrollX + rect.width / 2,
			top,
			transform: 'translateX(-50%)',
			zIndex: 1000,
			backgroundColor: 'rgba(0, 0, 0, 0.6)',
			color: 'white',
			padding: '6px 10px',
			borderRadius: 4,
			fontSize: 12,
			lineHeight: 1.3,
			maxWidth: 300,
			whiteSpace: 'nowrap',
			wordBreak: 'normal',
			pointerEvents: 'none',
			boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
			textAlign: 'center',
		})
	}

	useEffect(() => {
		if (shouldShowTooltip && showTooltip) {
			updateTooltipPosition()
			window.addEventListener('scroll', updateTooltipPosition)
			window.addEventListener('resize', updateTooltipPosition)
		}

		return () => {
			window.removeEventListener('scroll', updateTooltipPosition)
			window.removeEventListener('resize', updateTooltipPosition)
		}
	}, [shouldShowTooltip, tooltipPosition, showTooltip])

	const handleMouseEnter = () => {
		const el = textRef.current
		if (!el) return

		const isOverflowed = el.scrollWidth > el.offsetWidth
		setIsTruncated(isOverflowed)
		setShowTooltip(true)
	}

	return (
		<>
			<span
				ref={textRef}
				className={cn('truncate inline-block items-center', className)}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={() => setShowTooltip(false)}
			>
				{leftIcon && (
					<span className="inline-flex items-center justify-center mr-0.5 align-sub">
						{leftIcon}
					</span>
				)}{' '}
				{text}
			</span>

			{shouldShowTooltip &&
				showTooltip &&
				label &&
				createPortal(<div style={tooltipStyle}>{label}</div>, document.body)}
		</>
	)
}

export default EllipsisText
